import QueryString from 'qs';
import { isArray } from 'lib';
import { getEnv } from './getEnv';

export const getSmallLogoURL = () =>
  `${process.env.PUBLIC_URL}/${getEnv('REACT_APP_NAME')}/logo-small.${
    getEnv('REACT_APP_LOGO_TYPE') || 'svg'
  }`;

export const getLargeLogoURL = () =>
  `${process.env.PUBLIC_URL}/${getEnv('REACT_APP_NAME')}/logo-large.${
    getEnv('REACT_APP_LOGO_TYPE') || 'svg'
  }`;

export const getSecondaryLogoURL = () =>
  `${process.env.PUBLIC_URL}/${getEnv('REACT_APP_NAME')}/logo-secondary.${
    getEnv('REACT_APP_LOGO_TYPE') || 'svg'
  }`;

const isVip = (grade) => grade?.toLowerCase() === 'vip_with_photo';

/**
 * Generates a proxied image URL or returns the original URL based on proxy configuration.
 * This function is used to handle internet connectivity issues by routing requests through a proxy server.
 *
 * @param {string} url - The original image URL to be processed
 * @param {Object} [options] - Optional parameters for URL generation
 * @param {boolean} [options.isNotBase64=false] - Set to true if URL is not base64 encoded (adds is_not_base64=1 parameter)
 * @param {boolean} [options.noProxy=false] - Set to true to bypass proxy (adds no_proxy=1 parameter). Should be set to false in case of internet connection issues
 * @returns {string} The proxied URL if proxy is enabled, otherwise returns the original URL
 *
 * @example
 * // Basic usage with proxy enabled
 * getProxiedImage('https://example.com/image.jpg')
 * // Returns: 'https://api.example.com/media/download?url=aHR0cHM6Ly9leGFtcGxlLmNvbS9pbWFnZS5qcGc='
 *
 * @example
 * // Usage with options
 * getProxiedImage('https://example.com/image.jpg', { isNotBase64: true, noProxy: true })
 * // Returns: 'https://api.example.com/media/download?url=aHR0cHM6Ly9leGFtcGxlLmNvbS9pbWFnZS5qcGc=&is_not_base64=1&no_proxy=1'
 */
export const getProxiedImage = (url, options = {}) => {
  const useProxy = !!getEnv('REACT_APP_USE_PROXY');

  if (!url || !useProxy) {
    return url;
  }

  if (useProxy) {
    const baseUrl = `${getEnv(
      'REACT_APP_MEDIA_BASE_URL'
    )}/media/download?url=${window.btoa(url)}`;

    const queryParams = {};

    if (options.isNotBase64) {
      queryParams.is_not_base64 = '1';
    }

    if (options.noProxy) {
      queryParams.no_proxy = '1';
    }

    const queryString = QueryString.stringify(queryParams);
    return queryString ? `${baseUrl}&${queryString}` : baseUrl;
  }

  return url;
};

export const getImage = ({
  data,
  platform,
  cdn = false,
  sendPlatform = null,
  isVideo = false,
}) => {
  const useProxy = !!getEnv('REACT_APP_USE_PROXY');

  switch (platform) {
    case 'news':
      return getProxiedImage(data);
    case 'newspaper':
      return getProxiedImage(
        useProxy
          ? data
          : `${getEnv('REACT_APP_CDN_BASE_URL')}/download?url=${window.btoa(
              data
            )}`
      );
    case 'story': {
      const storyImage = JSON.parse(data?.images || '[]');

      if (isArray(storyImage?.candidates)) {
        let biggestImage =
          storyImage?.candidates?.[0]?.width *
          storyImage?.candidates?.[0]?.height;
        let biggestImageId = 0;

        storyImage?.candidates?.map((item, index) => {
          if (
            item.width * item?.height > biggestImage &&
            item.width !== item?.height
          ) {
            biggestImageId = index;
            biggestImage = item.width * item?.height;
          }
        });

        if (cdn)
          return [
            getProxiedImage(storyImage?.candidates?.[biggestImageId]?.url),
          ];

        return [
          getProxiedImage(
            `${getEnv('REACT_APP_CDN_BASE_URL')}/download?url=${window.btoa(
              isVideo
                ? data?.video_url
                : storyImage?.candidates?.[biggestImageId]?.url
            )}${sendPlatform ? `&platform=${sendPlatform}` : ''}`
          ),
        ];
      }

      return null;
    }

    case 'instagram': {
      const parsed = JSON.parse(data?.slides || data?.images || '[]');
      const items = isArray(parsed) ? parsed : [parsed];

      const coverImages = [];

      items?.forEach((item) => {
        const candidates =
          item?.image_versions2?.candidates || item?.candidates;
        if (isArray(candidates) && candidates.length > 0) {
          let biggestImageWidth = candidates[0].width;
          let biggestImageId = 0;

          candidates.map((item, index) => {
            if (item.width > biggestImageWidth) {
              biggestImageId = index;
              biggestImageWidth = item?.width;
            }
          });

          if (cdn) {
            coverImages.push(getProxiedImage(candidates?.[biggestImageId].url));
          } else {
            coverImages.push(
              getProxiedImage(
                `${getEnv('REACT_APP_CDN_BASE_URL')}/download?url=${window.btoa(
                  candidates?.[biggestImageId].url
                )}${
                  sendPlatform || isVip(data?.grade)
                    ? `&platform=${
                        isVip(data?.grade) ? 'instagram_vip' : sendPlatform
                      }`
                    : ''
                }`
              )
            );
          }
        }
      });

      return coverImages.length > 0 ? coverImages : null;
    }

    case 'virasty':
    case 'twitter': {
      const twitterPhotos = [];
      const twitterVideos = [];
      const media = JSON.parse(data?.media || '[]');

      media?.map((item) => {
        if (item?.type?.indexOf('photo') > -1) {
          if (cdn) {
            twitterPhotos.push(getProxiedImage(item.media_url_https));
          } else {
            twitterPhotos.push(
              getProxiedImage(
                `${getEnv('REACT_APP_CDN_BASE_URL')}/download?url=${window.btoa(
                  item.media_url_https
                )}${isVip(data?.grade) ? `&platform=${platform}_vip` : ''}`
              )
            );
          }
        }
        if (item?.type?.indexOf('video') > -1) {
          if (cdn) {
            twitterVideos.push({
              poster: getProxiedImage(item.media_url_https),
              video: item?.video_info?.variants?.[0]?.url,
              type: item?.video_info?.variants?.[0]?.content_type,
            });
          } else {
            twitterVideos.push({
              poster: getProxiedImage(
                `${getEnv('REACT_APP_CDN_BASE_URL')}/download?url=${window.btoa(
                  item.media_url_https
                )}${isVip(data?.grade) ? `&platform=${platform}_vip` : ''}`
              ),
              video: item?.video_info?.variants?.[0]?.url,
              type: item?.video_info?.variants?.[0]?.content_type,
            });
          }
        }
      });

      return { photos: twitterPhotos, videos: twitterVideos };
    }
    case 'telegram': {
      if (isVip(data?.grade) && data?.message_type === 'photo') {
        return getProxiedImage(
          `${getEnv('REACT_APP_CDN_BASE_URL')}/download?post_id=${
            data?.id
          }&platform=telegram_channels`
        );
      }

      if (data?.message_type === 'photo') {
        return getProxiedImage(
          `${getEnv('REACT_APP_MEDIA_BASE_URL')}/media/?url=https://t.me/${
            data?.username
          }/${data?.post_id}&type=image`
        );
      }

      return null;
    }
    default:
      return null;
  }
};
